import NextLink from 'next/link'

import GoGoPoolNotice from './GoGoPoolNotice'

import { useValueTokens } from '@/contexts/VaultTokensContext'
import { Chip, FOCUS, Panel } from '@/design-system/ui'

type Step = { title: string; body: string }

const HEADINGS: Record<string, string> = {
  avax: 'Put idle AVAX to work, keep it liquid',
  ggp: 'Withdrawing from the GGP vault',
}

const STEPS: Record<string, Step[]> = {
  avax: [
    {
      title: 'Deposit AVAX, get seaAVAX',
      body: 'You receive seaAVAX, an ERC-4626 share token, straight to your wallet. There is no lockup and nothing to claim.',
    },
    {
      title: 'Principal-protected yield',
      body: 'The vault puts your AVAX to work in a principal-protected yield strategy on Avalanche, aiming to earn steadily while protecting your deposit.',
    },
    {
      title: 'Yield compounds into the price',
      body: 'Returns flow back into the vault and lift the seaAVAX share price, so every share is steadily worth more AVAX.',
    },
  ],
  ggp: [
    {
      title: 'Deposits are closed',
      body: 'GoGoPool is winding down, so the vault no longer accepts new GGP. Existing positions are being unwound in an orderly way.',
    },
    {
      title: 'Withdraw whenever you like',
      body: 'Your xGGP stays in your wallet. Redeem it for GGP any time the pool holds liquidity; there is no lockup.',
    },
    {
      title: 'Rewards until fully unwound',
      body: 'Until positions are unwound, xGGP keeps its share of validator rewards, so the share price can still rise.',
    },
  ],
}

export default function HowItWorks() {
  const { currentToken } = useValueTokens()
  if (!currentToken) return null

  const symbol = currentToken.displayName
  const isAvax = currentToken.value === 'avax'
  const steps = STEPS[currentToken.value]
  const tokenAddress = currentToken.tokenAddresses[1][43114] as string

  return (
    <div className="lg:gap-8 flex flex-col gap-6">
      {!isAvax && <GoGoPoolNotice />}

      <Panel>
        <div className="rng-labelcaps">How it works</div>
        <h2
          className="mt-2"
          style={{
            fontFamily: 'var(--font-sans)',
            fontSize: 'clamp(23px, 2.4vw, 27px)',
            fontWeight: 400,
            letterSpacing: 0,
          }}
        >
          {HEADINGS[currentToken.value]}
        </h2>

        <div className="mt-8 grid gap-8 sm:grid-cols-3">
          {steps.map((step, i) => (
            <div key={step.title}>
              <div
                className="grid place-items-center rounded-full tabular-nums"
                style={{
                  background: 'var(--surface-quiet)',
                  color: 'var(--ink)',
                  fontSize: 16,
                  height: 32,
                  width: 32,
                }}
              >
                {i + 1}
              </div>
              <h3
                className="mt-4"
                style={{ fontFamily: 'var(--font-sans)', fontSize: 17, fontWeight: 400 }}
              >
                {step.title}
              </h3>
              <p className="mt-2" style={{ color: 'var(--muted)', fontSize: 13, lineHeight: 1.6 }}>
                {step.body}
              </p>
            </div>
          ))}
        </div>

        <div className="my-8" style={{ borderTop: '1px solid var(--rule-light)' }} />

        <h3 style={{ fontFamily: 'var(--font-sans)', fontSize: 18, fontWeight: 400 }}>
          Liquidity &amp; withdrawals
        </h3>
        <p
          className="mt-3"
          style={{ color: 'var(--muted)', fontSize: 13, lineHeight: 1.65, maxWidth: '72ch' }}
        >
          Redeem your shares for {symbol} any time the pool has liquidity. If a large or urgent
          redemption is short on liquidity, use <b>Request redemption</b> on the card: we re-add
          liquidity within <b>24 hours</b>, then you come back and complete the withdrawal here.
        </p>
      </Panel>

      {isAvax && (
        <div className="lg:p-6 p-5" style={{ background: 'var(--accent-wash)', borderRadius: 20 }}>
          <h3 style={{ fontFamily: 'var(--font-sans)', fontSize: 17, fontWeight: 400 }}>
            New strategy
          </h3>
          <p
            className="mt-2"
            style={{ color: 'var(--body)', fontSize: 13, lineHeight: 1.6, maxWidth: '74ch' }}
          >
            GoGoPool has{' '}
            <a
              className="underline"
              href="https://eol.gogopool.com/"
              rel="noopener noreferrer"
              style={{ color: 'var(--accent)', fontWeight: 500, textUnderlineOffset: 2 }}
              target="_blank"
            >
              shut down
            </a>
            , so the AVAX Vault no longer runs validators. It now earns through a{' '}
            <b>principal-protected yield strategy on Avalanche</b>, aiming for steady returns while
            protecting your deposit. Not financial advice; no yield is guaranteed.
          </p>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-3">
        <NextLink className={`no-underline ${FOCUS}`} href="/docs/#security">
          <Chip dot fill>
            Audited
          </Chip>
        </NextLink>
        <Chip dot fill>
          ERC-4626 standard
        </Chip>
        <Chip dot fill>
          2-of-3 multisig
        </Chip>
        <a
          className={`no-underline ${FOCUS}`}
          href={`https://snowtrace.io/token/${tokenAddress}`}
          rel="noopener noreferrer"
          target="_blank"
        >
          <Chip dot fill>
            Contract ↗
          </Chip>
        </a>
        <NextLink className={`no-underline sm:ml-auto ${FOCUS}`} href="/docs/">
          <Chip ink>Read the full docs ↗</Chip>
        </NextLink>
      </div>
    </div>
  )
}
